import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

/** V2 LoginForm — no framer-motion */

export default function LoginForm() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [sessionMsg, setSessionMsg] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') {
      setSessionMsg('Your session expired. Please sign in again.');
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const canSubmit = email.trim() !== '' && password !== '' && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email.trim() === '' || password === '') {
      setError('Please enter both your email and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (['org_admin', 'platform_admin', 'event_admin'].includes(user.role)) navigate('/dashboard');
      else if (user.role === 'volunteer') navigate('/scan');
      else if (user.role === 'sponsor')   navigate('/sponsor');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(140deg, #1B0E2C 0%, #140A20 55%, #2A0D3A 100%)',
      padding: 24, position: 'relative', overflow: 'hidden',
    }}>
      {/* Background glows */}
      <div style={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, #7B2FE4 0%, transparent 70%)', opacity: 0.18, top: -100, left: -80, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, #F5176B 0%, transparent 70%)', opacity: 0.14, bottom: -60, right: -60, pointerEvents: 'none' }} />

      <div style={{
        background: 'var(--surface, #fff)',
        borderRadius: 24, padding: '44px 40px',
        width: '100%', maxWidth: 420,
        boxShadow: '0 28px 72px rgba(0,0,0,0.36)',
        position: 'relative', zIndex: 1,
        animation: 'loginSlideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 60, height: 60, borderRadius: 16, margin: '0 auto 14px',
            background: 'linear-gradient(115deg, #7B2FE4 0%, #C71D8E 52%, #F5176B 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 10px 28px rgba(200,30,120,0.35)',
          }}>
            <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
              <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle"
                fill="#fff" fontSize="16" fontWeight="800"
                fontFamily="'Plus Jakarta Sans', sans-serif">CG</text>
            </svg>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink, #1A1222)', margin: 0 }}>Click n Go</h1>
          <p style={{ color: 'var(--ink-2, #5C5365)', fontSize: 14, margin: '5px 0 0' }}>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink, #1A1222)', marginBottom: 6 }}>
              Email address
            </label>
            <input
              type="email" value={email} required
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="inp"
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 22 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink, #1A1222)', marginBottom: 6 }}>
              Password
            </label>
            <input
              type="password" value={password} required
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="inp"
            />
          </div>

          {/* Session expired notice */}
          {sessionMsg && (
            <div style={{
              background: 'var(--warning-bg, #FFF7E6)', border: '1px solid var(--warning, #D98200)',
              borderRadius: 10, padding: '10px 14px', color: 'var(--warning-ink, #7A5C00)',
              fontSize: 13, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center',
            }}>
              🔒 {sessionMsg}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              background: 'var(--danger-bg, #FCEAEC)', border: '1px solid var(--danger, #E23145)',
              borderRadius: 10, padding: '10px 14px', color: 'var(--danger-ink, #B11226)',
              fontSize: 13, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center',
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn btn--primary btn--block"
            style={{ opacity: canSubmit ? 1 : 0.7, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>

      <style>{`
        @keyframes loginSlideUp {
          from { opacity: 0; transform: translateY(28px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .inp {
          width: 100%;
          padding: 12px 14px;
          font-family: var(--font, 'Plus Jakarta Sans', sans-serif);
          font-size: 15px;
          color: var(--ink, #1A1222);
          background: var(--bg, #FAF7FD);
          border: 1.5px solid var(--line-2, #E0D8EA);
          border-radius: 10px;
          outline: none;
          box-sizing: border-box;
          transition: border-color .15s, box-shadow .15s;
        }
        .inp:focus {
          border-color: var(--primary, #C81E78);
          box-shadow: 0 0 0 4px var(--primary-50, #FCEAF4);
        }
      `}</style>
    </div>
  );
}
